import moment from 'moment';
import root from 'window-or-global';
import {dateFormatter as epicDateFormatter} from '@epic-visualizations/charts';

import {weeklyEndDate, monthlyEndDate} from '../../../global-date-picker';
import {
    VERSION_TICK_FORMAT,
    DATE_FORMAT,
    PERCENT_FORMAT_DOUBLE_FRACTION,
    TimeUnit
} from './constants';
import {ActivationsColorPalette} from '../../../../constants/fortnite-games/ColorSwatch';
import {TimeScale, DateRange, DateMoment, Version} from '../../../../types';

const ACTIVATIONS_COLOR_PALETTE_LENGTH = ActivationsColorPalette.length;

export const getWidgetTimeRangeDates = (timeScale: TimeScale | string): DateRange => {
    const dateNow = moment();
    const startDate = moment(dateNow);

    switch (timeScale) {
        case TimeScale.HOURLY:
            return {
                startDate: startDate.subtract(24, 'hours').startOf('hour'),
                endDate: dateNow.startOf('hour'),
                selectedDate: dateNow.startOf('hour')
            };

        case TimeScale.DAILY:
            return {
                startDate: startDate.subtract(14, 'days'),
                endDate: dateNow,
                selectedDate: dateNow
            };

        case TimeScale.WEEKLY:
            return {
                startDate: startDate.subtract(4, 'weeks').startOf('week'),
                endDate: weeklyEndDate(dateNow),
                selectedDate: weeklyEndDate(dateNow)
            };

        case TimeScale.MONTHLY:
            return {
                startDate: startDate.subtract(3, 'months').startOf('month'),
                endDate: monthlyEndDate(dateNow),
                selectedDate: monthlyEndDate(dateNow)
            };

        default:
            return {
                startDate: dateNow,
                endDate: dateNow,
                selectedDate: dateNow
            };
    }
};

export const getEntryTime = (date: string, timescale: TimeScale): string => {
    const timeUnit = TimeUnit[timescale];

    const entryTime = moment(new Date(date))
        .startOf(timeUnit)
        .format(VERSION_TICK_FORMAT);

    return entryTime;
};

export const getVersionTicks = (versions: Version[], timescale: TimeScale): string[] => {
    const versionTicks = Array.from(
        new Set(
            versions.map(({displaced, activated}) =>
                getEntryTime(displaced || activated, timescale)
            )
        )
    );

    return versionTicks;
};

const displaceVersion = (version: Version, xAxisTicks: string[], timescale: TimeScale): Version => {
    const {activated} = version;

    const newVersionDate = xAxisTicks.find(xAxisTick => moment(xAxisTick).isAfter(activated));

    return {
        ...version,
        displaced: getEntryTime(newVersionDate, timescale)
    };
};

export const formatVersion = (
    version: Version,
    xAxisTicks: string[],
    timescale: TimeScale
): Version =>
    xAxisTicks.includes(getEntryTime(version.activated, timescale))
        ? version
        : displaceVersion(version, xAxisTicks, timescale);

const inferDate = (value: Date | string | number): Date =>
    value instanceof Date ? value : new Date(value);

export const dateFormatter = (formatOptions: object = DATE_FORMAT) => (locale?: string) => {
    const formatter = new Intl.DateTimeFormat(locale, formatOptions);

    return (value: DateMoment | string | number): string => {
        if (moment(value).isValid()) {
            const date = inferDate(value as Date);

            return formatter.format(date);
        } else {
            return value as string;
        }
    };
};

export const timeFormatter = (locale?: string) => {
    const formatter = new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: 'numeric'
    });

    return date => {
        if (moment(date).isValid()) {
            return formatter.format(new Date(date));
        } else {
            return date;
        }
    };
};

export const dateTimeFormatter = epicDateFormatter(undefined, {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false
});

export const percentFormatter = (
    locale: string,
    format?: {style: string; maximumFractionDigits?: number}
) => (value: number): string => {
    const formatter = new Intl.NumberFormat(locale, format || PERCENT_FORMAT_DOUBLE_FRACTION);

    return formatter.format(value / 100);
};

export const axisPercentFormatter = (locale?: string) => (value: number): string => {
    const formatter = new Intl.NumberFormat(locale, {
        style: 'percent',
        maximumFractionDigits: value < 1 ? 4 : 1
    });

    return formatter.format(value / 100);
};

export const axisDateFormatter = (locale?: string) => (value: Date | string | number) => {
    /*
     * In RTL language there is &rlm; unicode that broke layout
     * Here we remove it in case if there are regular numbers
     * And let it as it is in case of Western Arabic numbers
     *
     * Todo: Find better solution
     * */
    if (moment(value).isValid()) {
        const result = epicDateFormatter(locale, {
            month: '2-digit',
            day: '2-digit'
        })(value);

        return result.match(/^\d/) ? result.replace(/[\u200f]/g, '') : result;
    } else {
        return value;
    }
};

export const isTouchDevice = () => 'ontouchstart' in root || root.navigator.msMaxTouchPoints;

export const getIsLast24Hours = ({startDate, endDate, selectedDate}: DateRange) => {
    const dateNow = moment();

    return (
        dateNow.isSame(startDate, 'day') &&
        dateNow.isSame(endDate, 'day') &&
        dateNow.isSame(selectedDate, 'day')
    );
};

export const getColorIndex = (index: number): number =>
    Math.round(index % ACTIVATIONS_COLOR_PALETTE_LENGTH);
